<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t("item") }}</h4>
        </div>
      </div>

      <div class="page-btn d-flex gap-2 item-io-btns">
        <button
          v-if="can('inventory.item', 'R')"
          type="button"
          class="btn btn-item-export"
          :disabled="exporting"
          @click="exportItems"
        >
          <span v-if="exporting" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="ti ti-database-export me-1"></i>{{ $t("export_item") }}
        </button>
        <router-link v-if="can('inventory.item', 'W')" to="/item/import" class="btn btn-item-import">
          <i class="ti ti-database-import me-1"></i>{{ $t("import_item") }}
        </router-link>
        <router-link v-if="can('inventory.item', 'W')" to="/item/create" class="btn btn-added">
          <i class="ti ti-circle-plus me-2 text-white"></i>{{ $t("add_new_item") }}
        </router-link>
      </div>
    </div>

    <!-- /item list -->
    <ThemeDataTable
      ref="itemTable"
      table-id="ItemTable"
      :fetch-data="fetchItemsPage"
      :filters="tableFilters"
    >
      <template #default="{ rows }">
        <thead class="thead-light">
          <tr>
            <th>{{ $t("item_code") }}</th>
            <th>{{ $t("item_name") }}</th>
            <th>{{ $t("item_type") }}</th>
            <th>{{ $t("category") }}</th>
            <th>{{ $t("sub_category") }}</th>
            <th>{{ $t("model_number") }}</th>
            <th>{{ $t("unit") }}</th>
            <th>{{ $t("reorder_level") }}</th>
            <th>{{ $t("barcode_qr_code") }}</th>
            <th>{{ $t("selling_price") }}</th>
            <th>{{ $t("discount") }}</th>
            <th>{{ $t("vat_group") }}</th>
            <th>{{ $t("stock_quantity") }}</th>
            <th class="no-sort text-end">{{ $t("action") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in rows" :key="item.id">
            <td>{{ item.item_code }}</td>
            <td>
              <div class="d-flex align-items-center">
                <span class="avatar avatar-md me-2">
                  <img
                    v-if="item.images && item.images.length"
                    :src="item.images[0].small_thumb"
                    alt="Item Image"
                  />
                  <span
                    v-else
                    class="avatar-title rounded bg-light text-secondary d-flex align-items-center justify-content-center w-100 h-100"
                  >
                    <i class="ti ti-photo"></i>
                  </span>
                </span>
                <span>{{ item.item_name }}</span>
              </div>
            </td>
            <td>{{ item.item_type_name || '—' }}</td>
            <td>{{ item.category_name }}</td>
            <td>{{ item.sub_category_name }}</td>
            <td>{{ item.model_number }}</td>
            <td>{{ item.unit_name }}</td>
            <td>{{ item.reorder_level }}</td>
            <td>{{ item.barcode_qr_code }}</td>
            <td>{{ displaySellingPrice(item) }}</td>
            <td>{{ item.discount }}</td>
            <td>{{ item.vat_group_name }}</td>
            <td>{{ item.stock_quantity }}</td>

            <td class="action-table-data text-end">
              <div class="edit-delete-action justify-content-end item-row-actions">
                <router-link
                  v-if="can('inventory.item', 'E')"
                  :to="{ name: 'EditItem', params: { id: item.id } }"
                  class="me-2 p-2"
                  :title="$t('edit_item')"
                >
                  <i class="ti ti-edit"></i>
                </router-link>
                <span v-else class="me-2 p-2 action-slot" aria-hidden="true"></span>
                <a
                  v-if="can('inventory.item', 'R')"
                  href="javascript:void(0);"
                  class="me-2 p-2"
                  :title="$t('life_cycle')"
                  @click.prevent="openLifeCycle(item)"
                >
                  <i class="ti ti-history"></i>
                </a>
                <span v-else class="me-2 p-2 action-slot" aria-hidden="true"></span>
                <a
                  v-if="can('inventory.item', 'R') && !showVariantDetails(item)"
                  href="javascript:void(0);"
                  class="me-2 p-2"
                  :title="$t('batch_details')"
                  @click.prevent="openBatchDetails(item)"
                >
                  <i class="ti ti-packages"></i>
                </a>
                <span v-else class="me-2 p-2 action-slot" aria-hidden="true"></span>
                <a
                  v-if="showVariantDetails(item)"
                  href="javascript:void(0);"
                  class="me-2 p-2"
                  :title="$t('view_variants')"
                  @click.prevent="openVariantDetails(item)"
                >
                  <i class="ti ti-list-details"></i>
                </a>
                <span v-else class="me-2 p-2 action-slot" aria-hidden="true"></span>
              </div>
            </td>
          </tr>
        </tbody>
      </template>
    </ThemeDataTable>
    <!-- /item list -->

    <VariantDetailsModal
      :item-id="selectedVariantItemId"
      @life-cycle="openLifeCycleFromVariant"
      @batch-details="openBatchDetailsFromVariant"
    />
    <ItemLifeCycleModal
      :item-id="selectedLifeCycleItemId"
      :variant-id="selectedLifeCycleVariantId"
    />
    <ItemBatchDetailsModal
      :item-id="selectedBatchItemId"
      :variant-id="selectedBatchVariantId"
    />
  </div>
</template>

<script>
import { can as canAccess } from "@/helpers/permission";
import apiUrls from '@/urls';
import ThemeDataTable from '@/components/common/ThemeDataTable.vue';
import VariantDetailsModal from '@/views/admin_dashboard/item/VariantDetailsModal.vue';
import ItemLifeCycleModal from '@/views/admin_dashboard/item/ItemLifeCycleModal.vue';
import ItemBatchDetailsModal from '@/views/admin_dashboard/item/ItemBatchDetailsModal.vue';
import { truncateMoney2 } from '@/utils';

export default {
  components: {
    ThemeDataTable,
    VariantDetailsModal,
    ItemLifeCycleModal,
    ItemBatchDetailsModal,
  },
  data() {
    return {
      selectedVariantItemId: null,
      selectedLifeCycleItemId: null,
      selectedLifeCycleVariantId: null,
      selectedBatchItemId: null,
      selectedBatchVariantId: null,
      exporting: false,
      filterOptions: {
        item_type: [],
        category: [],
        sub_category: [],
        unit: [],
        vat_group: [],
      },
    };
  },

  computed: {
    tableFilters() {
      return [
        {
          key: 'item_type',
          label: this.$t('item_type'),
          options: this.filterOptions.item_type,
        },
        {
          key: 'category',
          label: this.$t('category'),
          options: this.filterOptions.category,
        },
        {
          key: 'sub_category',
          label: this.$t('sub_category'),
          options: this.filterOptions.sub_category,
        },
        {
          key: 'unit',
          label: this.$t('unit'),
          options: this.filterOptions.unit,
        },
        {
          key: 'vat_group',
          label: this.$t('vat_group'),
          options: this.filterOptions.vat_group,
        },
      ];
    },
  },

  methods: {
    formatMoney(amount) {
      return truncateMoney2(amount);
    },

    itemExportFilename(response) {
      const header = response?.headers?.['content-disposition'] || '';
      const star = /filename\*=(?:UTF-8''|utf-8'')([^;]+)/i.exec(header);
      const plain = /(?:^|;)\s*filename="?([^";]+)"?/i.exec(header);
      let name = (star && star[1]) || (plain && plain[1]) || '';
      try {
        name = decodeURIComponent(name.trim().replace(/^"|"$/g, ''));
      } catch (e) {
        name = name.trim().replace(/^"|"$/g, '');
      }
      if (name && !name.toLowerCase().endsWith('.xlsx')) {
        name = `${name.replace(/\.[^.]+$/, '')}.xlsx`;
      }
      if (!name) {
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const stamp = [
          pad(now.getDate()),
          pad(now.getMonth() + 1),
          now.getFullYear(),
          pad(now.getHours()),
          pad(now.getMinutes()),
          pad(now.getSeconds()),
        ].join('_');
        name = `Item_exported_at_${stamp}.xlsx`;
      }
      return name;
    },

    async exportItems() {
      this.exporting = true;
      try {
        const response = await this.$axios.get(apiUrls.item.export, {
          responseType: 'blob',
        });
        const contentType = (response.headers['content-type'] || '').toLowerCase();
        if (contentType.includes('application/json')) {
          const text = await response.data.text();
          const detail = JSON.parse(text);
          throw new Error(detail.error || detail.detail || 'Export failed');
        }
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.itemExportFilename(response);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Item export failed:', error);
        window.alert(error?.message || 'Item export failed. Please try again.');
      } finally {
        this.exporting = false;
      }
    },

    async fetchFilterOptions() {
      try {
        const [itemTypesRes, categoriesRes, subCategoriesRes, unitsRes, vatGroupsRes] = await Promise.all([
          this.$axios.get(apiUrls.item_type.get),
          this.$axios.get(apiUrls.categories.get),
          this.$axios.get(apiUrls.sub_categories.get),
          this.$axios.get(apiUrls.measurement_unit.get),
          this.$axios.get(apiUrls.vat_group.get),
        ]);

        this.filterOptions.item_type = (itemTypesRes?.data || [])
          .map((t) => t.name)
          .filter(Boolean);
        this.filterOptions.category = (categoriesRes?.data || [])
          .map((c) => c.node || c.name)
          .filter(Boolean);
        this.filterOptions.sub_category = (subCategoriesRes?.data || [])
          .map((c) => c.node || c.name)
          .filter(Boolean);
        this.filterOptions.unit = (unitsRes?.data || [])
          .map((u) => u.unit || u.name)
          .filter(Boolean);
        this.filterOptions.vat_group = (vatGroupsRes?.data || [])
          .map((v) => v.vat_group_name || v.name)
          .filter(Boolean);
      } catch (error) {
        console.error('Error fetching item filter options:', error);
      }
    },

    async fetchItemsPage({ draw, start, length, search, orderColumn, orderDir, filters }) {
      const params = {
        draw,
        start,
        length,
        'search[value]': search || '',
        'order[0][column]': orderColumn,
        'order[0][dir]': orderDir,
        ...filters,
      };

      const response = await this.$axios.get(apiUrls.item.get, { params });
      const payload = response.data || {};

      return {
        data: payload.data || [],
        recordsTotal: payload.recordsTotal || 0,
        recordsFiltered: payload.recordsFiltered || 0,
      };
    },

    can(code, requiredLevel) {
      return canAccess(code, requiredLevel);
    },

    showVariantDetails(item) {
      return item?.item_type_code === 'MULTI_VARIANT' || !!item?.has_variants;
    },

    displaySellingPrice(item) {
      if (this.showVariantDetails(item)) return '-';
      return this.formatMoney(item.selling_price);
    },

    openVariantDetails(item) {
      // Reset then set so reopening the same item still refetches
      this.selectedVariantItemId = null;
      this.$nextTick(() => {
        this.selectedVariantItemId = item.id;
        const modalEl = document.getElementById('variant-details-modal');
        if (!modalEl) return;
        const modal = bootstrap.Modal.getOrCreateInstance(modalEl);
        modal.show();
      });
    },

    openLifeCycle(item) {
      this.selectedLifeCycleItemId = null;
      this.selectedLifeCycleVariantId = null;
      this.$nextTick(() => {
        this.selectedLifeCycleItemId = item.id;
        this.selectedLifeCycleVariantId = null;
        const modalEl = document.getElementById('item-lifecycle-modal');
        if (!modalEl) return;
        const modal = bootstrap.Modal.getOrCreateInstance(modalEl);
        modal.show();
      });
    },

    openLifeCycleFromVariant({ itemId, variantId }) {
      this.selectedLifeCycleItemId = null;
      this.selectedLifeCycleVariantId = null;
      this.$nextTick(() => {
        this.selectedLifeCycleItemId = itemId;
        this.selectedLifeCycleVariantId = variantId;
        const modalEl = document.getElementById('item-lifecycle-modal');
        if (!modalEl) return;
        const modal = bootstrap.Modal.getOrCreateInstance(modalEl);
        modal.show();
      });
    },

    openBatchDetails(item) {
      this.selectedBatchItemId = null;
      this.selectedBatchVariantId = null;
      this.$nextTick(() => {
        this.selectedBatchItemId = item.id;
        this.selectedBatchVariantId = null;
        const modalEl = document.getElementById('item-batch-details-modal');
        if (!modalEl) return;
        const modal = bootstrap.Modal.getOrCreateInstance(modalEl);
        modal.show();
      });
    },

    openBatchDetailsFromVariant({ itemId, variantId }) {
      this.selectedBatchItemId = null;
      this.selectedBatchVariantId = null;
      this.$nextTick(() => {
        this.selectedBatchItemId = itemId;
        this.selectedBatchVariantId = variantId;
        const modalEl = document.getElementById('item-batch-details-modal');
        if (!modalEl) return;
        const modal = bootstrap.Modal.getOrCreateInstance(modalEl);
        modal.show();
      });
    },
  },

  created() {
    this.fetchFilterOptions();
  },
};
</script>

<style scoped>
:deep(table .edit-delete-action.justify-content-end) {
  justify-content: flex-end !important;
  -webkit-justify-content: flex-end !important;
  -ms-flex-pack: end !important;
}
:deep(td.action-table-data.text-end) {
  text-align: right !important;
}
:deep(table .edit-delete-action a),
:deep(table .edit-delete-action .action-slot) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  box-sizing: border-box;
  flex: 0 0 2rem;
}
:deep(table .edit-delete-action .action-slot) {
  visibility: hidden;
  pointer-events: none;
}
:deep(table .edit-delete-action .ti) {
  font-size: 1.05rem;
  line-height: 1;
}
.item-io-btns .btn {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  border-radius: 8px;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease,
    transform 0.1s ease;
}
.item-io-btns .btn i {
  font-size: 1.1rem;
  line-height: 1;
}
.item-io-btns .btn:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12);
}
.btn-item-export {
  background: #ecfdf5;
  border: 1px solid #10b981;
  color: #047857;
}
.btn-item-export:hover:not(:disabled) {
  background: #d1fae5;
  border-color: #059669;
  color: #065f46;
}
.btn-item-import {
  background: #eff6ff;
  border: 1px solid #3b82f6;
  color: #1d4ed8;
}
.btn-item-import:hover {
  background: #dbeafe;
  border-color: #2563eb;
  color: #1e40af;
}
</style>
